import { z } from 'zod';

/**
 * The semantic signals Jev is allowed to answer.
 *
 * Code-owned, not prompt-owned. Each signal names a narrow, observable fact about a
 * conversation and nothing else — the line the whole AI contract rests on is that a
 * signal describes what a message *says*, never what the product should *do* about it.
 * "Does the newest inbound message explicitly request cancellation?" is a signal.
 * "Should the cancellation workflow run?" is a policy decision and lives in code.
 *
 * Every signal is versioned. A change to a signal's question or its output shape is a new
 * version, because cached results from the old one are answers to a different question
 * and silently reusing them is how a threshold calibrated on one meaning ends up applied
 * to another.
 */

export const CLINIC_SIGNAL_NAMES = [
  'response_needed',
  'expected_owner',
  'deadline_mentioned',
  'message_kind',
  'ask_resolution',
] as const;

export type ClinicSignalName = (typeof CLINIC_SIGNAL_NAMES)[number];

/** A yes/no signal: a probability, plus the messages it was read from. */
export const NoulSignalValueSchema = z.object({
  kind: z.literal('noul'),
  /** Probability of yes, 0..1. Thresholds are applied by policy, never here. */
  probability: z.number().min(0).max(1),
});

/** A labelled choice: the label, its confidence, and the full distribution. */
export const ChoiceSignalValueSchema = z.object({
  kind: z.literal('choice'),
  choice: z.string(),
  confidence: z.number().min(0).max(1),
  probabilities: z.record(z.string(), z.number()),
});

export const SignalValueSchema = z.discriminatedUnion('kind', [
  NoulSignalValueSchema,
  ChoiceSignalValueSchema,
]);

export type SignalValue = z.infer<typeof SignalValueSchema>;

/**
 * A stored signal result.
 *
 * `evidenceMessageIds` is what makes a signal answerable to a human: every surfaced
 * outcome has to link back to the messages it was read from, and a result with no
 * evidence is an assertion nobody can check.
 */
export const SignalResultSchema = z.object({
  name: z.enum(CLINIC_SIGNAL_NAMES),
  version: z.number().int().positive(),
  value: SignalValueSchema,
  evidenceMessageIds: z.array(z.number().int().positive()),
  /** The hash of the projection this was computed from. See `conversationVersionHash`. */
  inputVersion: z.string(),
  model: z.string(),
});

export type SignalResult = z.infer<typeof SignalResultSchema>;

export type ClinicSignalDefinition = {
  name: ClinicSignalName;
  /**
   * Bumped whenever the question or the output shape changes. Cached results at an older
   * version are answers to a different question and are never reused.
   */
  version: number;
  kind: 'noul' | 'choice';
  /** The question, as it is put to the model. */
  instructions: string;
  /** For a choice signal, the labels and what each one means. */
  criteria?: Record<string, string>;
  /** Why this signal exists, in product terms. */
  purpose: string;
};

/**
 * The registry.
 *
 * Note what is absent: nothing here asks the model for a date, a duration, a ranking, a
 * threshold, or a decision about an action. Jev reports that a deadline was *mentioned*
 * and which message mentioned it; application code parses and compares the date. That
 * split is deliberate and load-bearing — a model doing arithmetic is a model that is
 * confidently wrong about a Tuesday.
 */
export const CLINIC_SIGNALS: Record<ClinicSignalName, ClinicSignalDefinition> = {
  response_needed: {
    name: 'response_needed',
    version: 1,
    kind: 'noul',
    instructions:
      'Does the newest inbound message require a response or some other action from the recipient?',
    purpose: 'Drives whether a conversation surfaces in the attention queue at all.',
  },
  expected_owner: {
    name: 'expected_owner',
    version: 1,
    kind: 'choice',
    instructions: 'Who is expected to act next in this conversation?',
    criteria: {
      team: 'The mailbox owner or their team must do something next.',
      counterparty: 'The sender or another external party must do something next.',
      unknown: 'The conversation does not establish who acts next.',
    },
    purpose: 'Separates "waiting on us" from "waiting on them" in the conversation state.',
  },
  deadline_mentioned: {
    name: 'deadline_mentioned',
    version: 1,
    kind: 'noul',
    instructions:
      'Does any message explicitly state a deadline, due date, or date by which something must happen? Answer only about whether one is stated, not about whether it has passed.',
    purpose:
      'Flags conversations worth parsing a date out of. The date itself is parsed and compared by application code.',
  },
  message_kind: {
    name: 'message_kind',
    version: 1,
    kind: 'choice',
    instructions: 'What kind of message is the newest inbound one?',
    criteria: {
      request: 'It asks the recipient for something, or expects them to act.',
      follow_up: 'It chases an earlier request that has not been answered.',
      reply: 'It answers something the recipient asked, and expects nothing further.',
      bulk: 'It is a newsletter, notification, receipt, or other automated bulk mail.',
      other: 'None of the above describes it.',
    },
    purpose: 'Distinguishes a chaser from a first request, and both from noise.',
  },
  ask_resolution: {
    name: 'ask_resolution',
    version: 1,
    kind: 'choice',
    instructions:
      'Considering the open request described, does the newest inbound message resolve it, change it, leave it open, or is that unclear?',
    criteria: {
      resolves: 'The newest message clearly satisfies or withdraws the request.',
      changes: 'The newest message changes what is being asked for.',
      leaves_open: 'The newest message does not address the request.',
      unclear: 'The message could be read either way.',
    },
    purpose:
      'Feeds the review state. `unclear` keeps the Ask open and marks the conversation for review rather than clearing it.',
  },
};

/** The signals worth computing for every materially changed conversation. */
export const ATTENTION_ROUTING_SIGNALS: readonly ClinicSignalName[] = [
  'response_needed',
  'expected_owner',
  'deadline_mentioned',
  'message_kind',
];
